import React, { useState } from 'react';
import { PRESET_TAGS } from './types';
import { memberAuthorFromProfile } from './communityUtils';
import { useCommunityForum } from './CommunityForumContext';
import { CommunityDevPath } from './CommunityDevUi';
import { P } from './communityDevMode';
import { useAuth } from '../../contexts/AuthContext';

type Props = {
  sectionId: string;
  sectionName: string;
  onClose: () => void;
  onCreated: (topicId: string) => void;
};

export default function CreateTopicModal({ sectionId, sectionName, onClose, onCreated }: Props) {
  const { user } = useAuth();
  const { addTopic, isAdmin } = useCommunityForum();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [anonymous, setAnonymous] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleTag = (tag: string) => {
    setTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag].slice(0, 5)));
  };

  const submit = async () => {
    if (!title.trim()) {
      setError('Укажите заголовок темы');
      return;
    }
    if (!description.trim()) {
      setError('Опишите контекст вопроса');
      return;
    }
    const topic = await addTopic({
      sectionId,
      title,
      description,
      tags,
      author: memberAuthorFromProfile(anonymous, user?.email),
    });
    if (topic) onCreated(topic.id);
    onClose();
  };

  return (
    <div className="dcf-modal-overlay" onClick={onClose} role="presentation">
      <div className="dcf-modal dcf-modal--wide" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="create-topic-title">
        <h2 id="create-topic-title" className="dcf-modal__title">Создать тему</h2>
        <p className="dcf-modal__hint">
          Раздел: {sectionName}
          <CommunityDevPath path={P.topicSectionId} inline />
        </p>

        <div className="dcf-field">
          <label className="dcf-field__label" htmlFor="topic-title">
            Заголовок темы
            <CommunityDevPath path={P.topicTitle} inline />
          </label>
          <input id="topic-title" className="dcf-input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>

        <div className="dcf-field">
          <label className="dcf-field__label" htmlFor="topic-desc">
            Описание / контекст вопроса
            <CommunityDevPath path={P.topicDescription} inline />
          </label>
          <textarea id="topic-desc" className="dcf-textarea" rows={5} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <div className="dcf-field">
          <span className="dcf-field__label">
            Теги
            <CommunityDevPath path={P.topicTags} inline />
          </span>
          <div className="dcf-tag-picker">
            {PRESET_TAGS.map((tag) => (
              <button
                key={tag}
                type="button"
                className={`dcf-tag dcf-tag--filter ${tags.includes(tag) ? 'dcf-tag--filter-active' : ''}`}
                onClick={() => toggleTag(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
          <p className="dcf-field__hint">До 5 тегов. Контекст темы задаётся через теги, а не через дробление разделов.</p>
        </div>

        <div className="dcf-field">
          <span className="dcf-field__label">
            Автор
            <CommunityDevPath path={P.topicAuthor} inline />
          </span>
          <label className="dcf-toggle-row">
            <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} />
            Опубликовать анонимно
          </label>
        </div>

        {!isAdmin && (
          <p className="mt-3 text-xs text-slate-500">
            В разделах объявлений темы могут создавать только администраторы.
          </p>
        )}

        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

        <div className="dcf-modal__actions">
          <button type="button" className="dcf-btn dcf-btn--secondary" onClick={onClose}>Отмена</button>
          <button type="button" className="dcf-btn dcf-btn--primary" onClick={submit}>Создать тему</button>
        </div>
      </div>
    </div>
  );
}
